import * as React from 'react';
import {Query} from 'react-apollo';
// @ts-expect-error
import type {QueryRenderProps} from 'react-apollo';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ListViewInfiniteLoading as ListViewLoading} from 'shells/list-view/list-view-infinite-loading';

import type {
    GetUserEmailSequenceTemplates as GetUserEmailSequenceTemplatesQuery,
    GetSharedEmailSequenceTemplates as GetSharedEmailSequenceTemplatesQuery,
    GetArchivedEmailSequenceTemplates as GetArchivedEmailSequenceTemplatesQuery,
    GetUserEmailSequenceTemplates_emailSequenceTemplateCollection_userTemplates as UserTemplateGroup,
    GetSharedEmailSequenceTemplates_emailSequenceTemplateCollection_sharedTemplates as SharedTemplateGroup,
    GetArchivedEmailSequenceTemplates_emailSequenceTemplateCollection_archivedTemplates as ArchivedTemplateGroup,
} from 'nutshell-graphql-types';

import GET_ARCHIVED_EMAIL_SEQUENCE_TEMPLATES from '../../graphql/queries/get-archived-email-sequence-templates.graphql';
import GET_SHARED_EMAIL_SEQUENCE_TEMPLATES from '../../graphql/queries/get-shared-email-sequence-templates.graphql';
import GET_USER_EMAIL_SEQUENCE_TEMPLATES from '../../graphql/queries/get-user-email-sequence-templates.graphql';

import {USER_TEMPLATES, SHARED_TEMPLATES, ARCHIVED_TEMPLATES} from './constants';
import type {SequenceTemplateType} from './constants';
import {TemplateListGroup} from './template-list-group';

import styles from './templates.module.css';

export type TemplateGroup = UserTemplateGroup[] | SharedTemplateGroup[] | ArchivedTemplateGroup[];
export type TemplateItem = TemplateGroup[number];

type OwnProps = {
    type: SequenceTemplateType;
    onSequenceRename: (sequenceId: string, currentName: string) => void;
};

type Props = OwnProps & {
    emailSequenceTemplates: TemplateGroup | null | undefined;
    errorMessage: string | null | undefined;
    isLoading: boolean | null | undefined;
};

export function TemplateListComponent(props: Props) {
    if (props.isLoading && !props.emailSequenceTemplates) {
        return (
            <div className={styles['sequence-templates']}>
                <ListViewLoading />
            </div>
        );
    }

    // TODO: better error state!
    if (props.errorMessage) {
        return props.errorMessage;
    }

    if (props.emailSequenceTemplates) {
        return (
            <div className={styles['sequence-templates']}>
                <TemplateListGroup
                    templateType={props.type}
                    templates={props.emailSequenceTemplates}
                    onSequenceRename={props.onSequenceRename}
                />
            </div>
        );
    }

    return null;
}

class UserTemplateQuery extends Query<GetUserEmailSequenceTemplatesQuery, Record<any, any>> {}
class SharedTemplateQuery extends Query<GetSharedEmailSequenceTemplatesQuery, Record<any, any>> {}
class ArchivedTemplateQuery extends Query<
    GetArchivedEmailSequenceTemplatesQuery,
    Record<any, any>
> {}

export const TemplateList = (props: OwnProps) => (
    <SharedTemplateQuery
        query={GET_SHARED_EMAIL_SEQUENCE_TEMPLATES}
        skip={props.type !== SHARED_TEMPLATES}
    >
        {(sharedTemplates) => (
            <ArchivedTemplateQuery
                query={GET_ARCHIVED_EMAIL_SEQUENCE_TEMPLATES}
                skip={props.type !== ARCHIVED_TEMPLATES}
            >
                {(archivedTemplates) => (
                    <UserTemplateQuery
                        query={GET_USER_EMAIL_SEQUENCE_TEMPLATES}
                        skip={props.type !== USER_TEMPLATES}
                    >
                        {(userTemplates) => {
                            const {templates, errorMessage, isLoading} = getResultsForType(
                                props.type,
                                {
                                    sharedTemplates,
                                    archivedTemplates,
                                    userTemplates,
                                }
                            );

                            return (
                                // @ts-expect-error
                                <TemplateListComponent
                                    {...props}
                                    errorMessage={errorMessage}
                                    isLoading={isLoading}
                                    emailSequenceTemplates={templates}
                                />
                            );
                        }}
                    </UserTemplateQuery>
                )}
            </ArchivedTemplateQuery>
        )}
    </SharedTemplateQuery>
);

function getResultsForType(
    type: SequenceTemplateType,
    results: {
        userTemplates:
            | QueryRenderProps<GetUserEmailSequenceTemplatesQuery, Record<any, any>>
            | null
            | undefined;
        sharedTemplates:
            | QueryRenderProps<GetSharedEmailSequenceTemplatesQuery, Record<any, any>>
            | null
            | undefined;
        archivedTemplates:
            | QueryRenderProps<GetArchivedEmailSequenceTemplatesQuery, Record<any, any>>
            | null
            | undefined;
    }
) {
    const typeResults = results[type];
    if (!typeResults) return {templates: null, errorMessage: null, isLoading: null};

    return {
        templates:
            typeResults.data &&
            typeResults.data.emailSequenceTemplateCollection &&
            typeResults.data.emailSequenceTemplateCollection[type],
        errorMessage: safelyGetGraphQLErrorMessage(typeResults.error),
        isLoading: typeResults.loading,
    };
}
